import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import ENV from 'react-native-config';
import { useSelector } from 'react-redux';

import {
    DidomiSDK as DidomiReactSDK,
    IDidomiConfig,
    IDidomiObject,
} from '@didomi/react';
import { md5 } from 'js-md5';

import { selectSharedConsentPublicId } from 'store/user/selectors';

import { DidomiInstance } from 'services/consents-management/DidomiSDK/types';
import { updateConsent } from 'services/consents-management/DidomiSDK/utils';

const didomiConfig: IDidomiConfig = {
    languages: {
        enabled: ['en', 'hu'],
        default: 'hu',
    },
    sync: {
        enabled: true,
        frequency: 86400,
        delayNotice: true,
    },
};

const DidomiSDK = forwardRef<DidomiInstance>(function DidomiSDK(_, ref) {
    const didomiWebInstance = useRef<IDidomiObject | undefined>(undefined);
    const sharedConsentPublicId = useSelector(selectSharedConsentPublicId);

    const [didomiIsReady, setDidomiIsReady] = useState(false);

    useImperativeHandle(ref, () => ({
        async showPreferences() {
            didomiWebInstance.current?.preferences.show();
        },
        async logout() {
            await didomiWebInstance.current?.clearUser();
            await didomiWebInstance.current?.reset();
        },
    }));

    const handleReady = useCallback((didomi) => {
        didomiWebInstance.current = didomi;
        setDidomiIsReady(true);
    }, []);

    useEffect(() => {
        if (didomiIsReady && sharedConsentPublicId) {
            window.Didomi?.setUser({
                organizationUserId: sharedConsentPublicId,
                organizationUserIdAuthDigest: md5(
                    sharedConsentPublicId.concat(ENV.DIDOMI_SECRET || ''),
                ),
                organizationUserIdAuthSid: ENV.DIDOMI_SECRET_ID,
                organizationUserIdAuthAlgorithm: 'hash-md5',
            });
            window.Didomi?.syncUser();
        }
    }, [didomiIsReady, sharedConsentPublicId]);

    return (
        <DidomiReactSDK
            config={didomiConfig}
            apiKey={ENV.DIDOMI_API_KEY}
            // noticeId={ENV.DIDOMI_WEB_NOTICE_ID}
            // ! Mocked DIDOMI_WEB_NOTICE_ID for testing
            noticeId="449gfQYH"
            onReady={handleReady}
            onConsentChanged={updateConsent}
        />
    );
});

export default DidomiSDK;
